"use client";

import { useMemo, useState, type ComponentType } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock,
  Gift as GiftIcon,
  Heart,
  ImageIcon,
  MessageCircle,
  Sparkles,
  Star,
  Stars,
  X,
} from "lucide-react";
import type { Gift } from "@/types";
import { cn } from "@/lib/utils";
import {
  getExperienceData,
  type GiftTreeExperienceData,
  type GiftTreeIcon,
} from "@/data/experienceDataSchema";

export type GiftTreeVariant = "default" | "spotify";

interface GiftTreePalette {
  accent: string;
  accentRgb: string;
}

interface GiftTreeSectionProps {
  gift: Partial<Gift>;
  variant: GiftTreeVariant;
  palette?: GiftTreePalette;
  className?: string;
}

type SymbolicGiftIcon = ComponentType<{ className?: string }>;

interface SymbolicGift {
  id: string;
  title: string;
  description: string;
  Icon: SymbolicGiftIcon;
}

const GIFT_TREE_ICON_MAP: Record<GiftTreeIcon, SymbolicGiftIcon> = {
  gift: GiftIcon,
  heart: Heart,
  star: Star,
  image: ImageIcon,
  clock: Clock,
};

function pickGiftTreeIcon(icon?: GiftTreeIcon): SymbolicGiftIcon {
  if (icon && icon in GIFT_TREE_ICON_MAP) return GIFT_TREE_ICON_MAP[icon];
  return GiftIcon;
}

// Presentes personalizados pelo usuário sobrescrevem a derivação automática.
function buildCustomGifts(
  items: GiftTreeExperienceData["gifts"]
): SymbolicGift[] | null {
  if (!Array.isArray(items) || items.length === 0) return null;
  const result: SymbolicGift[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i] ?? {};
    const title = item.title?.trim();
    const message = item.message?.trim();
    if (!title && !message) continue;
    result.push({
      id: `custom-${i}`,
      title: title || "Surpresa",
      description: message || "Um detalhe simbólico dessa história.",
      Icon: pickGiftTreeIcon(item.icon),
    });
  }
  return result.length > 0 ? result : null;
}

// Gera presentes simbólicos a partir somente dos dados que já existem no Gift.
// Não cria contrato novo, não inventa fatos pessoais e sempre garante pelo
// menos um presente de fallback para a árvore não ficar vazia.
function buildSymbolicGifts(gift: Partial<Gift>): SymbolicGift[] {
  const list: SymbolicGift[] = [];
  const hasPhotos = (gift.photos?.length ?? 0) > 0;
  const hasCoupleStart = Boolean(gift.coupleStartDate);
  const hasLetter = Boolean(
    gift.loveLetter && gift.loveLetter.trim().length > 0
  );

  if (hasPhotos) {
    list.push({
      id: "memorias",
      title: "Memórias",
      description:
        "Um presente feito com os momentos que vocês guardaram juntos.",
      Icon: ImageIcon,
    });
  }
  if (hasCoupleStart) {
    list.push({
      id: "tempo",
      title: "Tempo",
      description: "Cada dia dessa história virou parte do presente.",
      Icon: Clock,
    });
  }
  if (hasLetter) {
    list.push({
      id: "mensagem",
      title: "Mensagem",
      description:
        "Palavras escolhidas para transformar carinho em lembrança.",
      Icon: MessageCircle,
    });
  }
  list.push({
    id: "surpresa",
    title: "Surpresa",
    description:
      "Um detalhe simbólico para lembrar que o amor também vive nos pequenos gestos.",
    Icon: GiftIcon,
  });

  return list;
}

export function GiftTreeSection({
  gift,
  variant,
  palette,
  className,
}: GiftTreeSectionProps) {
  const customData = useMemo(
    () => getExperienceData<GiftTreeExperienceData>(gift, "gift-tree"),
    [gift]
  );
  const gifts = useMemo(() => {
    const custom = buildCustomGifts(customData?.gifts);
    if (custom) return custom;
    return buildSymbolicGifts(gift);
  }, [gift, customData]);
  const [openId, setOpenId] = useState<string | null>(null);
  const openGift = openId ? gifts.find((g) => g.id === openId) ?? null : null;
  const customTitle = customData?.title?.trim();
  const customSubtitle = customData?.subtitle?.trim();

  if (variant === "spotify") {
    return (
      <SpotifyVariant
        palette={palette ?? { accent: "#1DB954", accentRgb: "29,185,84" }}
        gifts={gifts}
        openGift={openGift}
        openId={openId}
        onSelect={setOpenId}
        onClose={() => setOpenId(null)}
        className={className}
        title={customTitle}
        subtitle={customSubtitle}
      />
    );
  }

  return (
    <DefaultVariant
      gifts={gifts}
      openGift={openGift}
      openId={openId}
      onSelect={setOpenId}
      onClose={() => setOpenId(null)}
      className={className}
      title={customTitle}
      subtitle={customSubtitle}
    />
  );
}

// ─── Variante default — luz romântica, premium ──────────────────────────────

interface VariantInnerProps {
  gifts: SymbolicGift[];
  openGift: SymbolicGift | null;
  openId: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
  className?: string;
  title?: string;
  subtitle?: string;
}

function DefaultVariant({
  gifts,
  openGift,
  openId,
  onSelect,
  onClose,
  className,
  title,
  subtitle,
}: VariantInnerProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        "gift-tree-default relative overflow-hidden rounded-3xl border shadow-sm",
        className
      )}
      style={{
        background:
          "linear-gradient(160deg, #FFE4E6 0%, #FFF7ED 50%, #FFE4F1 100%)",
        borderColor: "rgba(225,29,72,0.18)",
      }}
    >
      <DefaultDecoration />

      <div className="relative z-10 flex flex-col gap-6 p-6 sm:p-8">
        <header className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337] shadow-sm backdrop-blur">
            <GiftIcon className="h-3 w-3 text-[#E11D48]" />
            Árvore de presentes
          </span>
          <h2
            className="text-2xl sm:text-3xl font-semibold leading-tight text-[#1F2937]"
            style={{ fontFamily: "var(--font-serif, serif)" }}
          >
            {title || "Presentes que contam essa história"}
          </h2>
          <p className="max-w-md text-sm text-[#1F2937]/70">
            {subtitle ||
              "Toque em cada presente para revelar um detalhe simbólico que faz parte de vocês."}
          </p>
        </header>

        <DefaultTreeVisual
          gifts={gifts}
          openId={openId}
          onSelect={onSelect}
        />

        <AnimatePresence mode="wait">
          {openGift ? (
            <motion.div
              key={openGift.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="relative mx-auto w-full max-w-md rounded-2xl border border-white/80 bg-white/85 p-5 shadow-md backdrop-blur"
            >
              <button
                type="button"
                onClick={onClose}
                aria-label="Fechar presente"
                className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full text-[#881337]/70 transition hover:bg-[#FFE4E6] hover:text-[#881337] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E11D48]/40"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#E11D48]">
                <openGift.Icon className="h-3.5 w-3.5" />
                {openGift.title}
              </div>
              <p className="mt-2 pr-6 text-sm leading-relaxed text-[#1F2937]/85">
                {openGift.description}
              </p>
            </motion.div>
          ) : (
            <motion.p
              key="hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center text-[10px] uppercase tracking-[0.22em] text-[#881337]/60"
            >
              Escolha um presente para abrir
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.section>
  );
}

function DefaultTreeVisual({
  gifts,
  openId,
  onSelect,
}: {
  gifts: SymbolicGift[];
  openId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="relative mx-auto flex w-full max-w-sm flex-col items-center">
      <svg
        viewBox="0 0 200 220"
        width="200"
        height="220"
        aria-hidden="true"
        className="drop-shadow-[0_10px_30px_rgba(225,29,72,0.18)]"
      >
        <defs>
          <linearGradient
            id="gift-tree-default-fill"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor="#fda4af" />
            <stop offset="60%" stopColor="#fb7185" />
            <stop offset="100%" stopColor="#e11d48" />
          </linearGradient>
          <linearGradient
            id="gift-tree-default-trunk"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor="#a16207" />
            <stop offset="100%" stopColor="#78350f" />
          </linearGradient>
        </defs>

        <rect
          x="93"
          y="172"
          width="14"
          height="32"
          rx="3"
          fill="url(#gift-tree-default-trunk)"
        />

        <path
          d="M100 12 L150 70 L120 70 L162 130 L130 130 L175 178 L25 178 L70 130 L38 130 L80 70 L50 70 Z"
          fill="url(#gift-tree-default-fill)"
          stroke="rgba(255,255,255,0.85)"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />

        <g transform="translate(100 8)">
          <circle r="9" fill="#fde68a" opacity="0.55" />
          <path
            d="M0 -7 L1.6 -2.2 L6.6 -2.2 L2.5 0.7 L4.1 5.5 L0 2.6 L-4.1 5.5 L-2.5 0.7 L-6.6 -2.2 L-1.6 -2.2 Z"
            fill="#f59e0b"
          />
        </g>

        {[
          { cx: 74, cy: 92, r: 2.2, fill: "#fde68a", delay: 0 },
          { cx: 124, cy: 86, r: 1.8, fill: "#ffffff", delay: 0.4 },
          { cx: 60, cy: 146, r: 2.2, fill: "#ffffff", delay: 0.8 },
          { cx: 140, cy: 150, r: 2.2, fill: "#fde68a", delay: 1.2 },
          { cx: 100, cy: 110, r: 1.8, fill: "#ffffff", delay: 0.6 },
          { cx: 100, cy: 56, r: 1.8, fill: "#fde68a", delay: 1.0 },
        ].map((light, i) => (
          <motion.circle
            key={i}
            cx={light.cx}
            cy={light.cy}
            r={light.r}
            fill={light.fill}
            animate={{ opacity: [0.45, 1, 0.45] }}
            transition={{
              duration: 2.6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: light.delay,
            }}
          />
        ))}
      </svg>

      <div className="mt-4 grid w-full grid-cols-2 gap-3 sm:grid-cols-4">
        {gifts.map((g) => {
          const isActive = openId === g.id;
          const Icon = g.Icon;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => onSelect(g.id)}
              aria-label={`Abrir presente: ${g.title}`}
              aria-pressed={isActive}
              className={cn(
                "group relative flex flex-col items-center justify-center gap-1 rounded-2xl border px-3 py-3 text-center transition-all",
                "border-white/70 bg-white/70 text-[#881337] shadow-sm backdrop-blur",
                "hover:-translate-y-0.5 hover:bg-white/90 hover:shadow-md",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E11D48]/40",
                isActive &&
                  "-translate-y-0.5 bg-white shadow-md ring-2 ring-[#E11D48]/60"
              )}
            >
              <span
                className={cn(
                  "inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-white/80 shadow-inner transition-colors",
                  isActive
                    ? "bg-gradient-to-br from-[#E11D48] to-[#881337] text-white"
                    : "bg-gradient-to-br from-[#FFE4E6] to-[#fda4af] text-[#881337]"
                )}
              >
                <Icon className="h-4 w-4" />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.16em]">
                {g.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DefaultDecoration() {
  return (
    <>
      <Heart
        className="absolute -top-4 -right-4 h-24 w-24 text-[#E11D48]/15"
        fill="currentColor"
      />
      <Heart
        className="absolute -bottom-3 -left-3 h-14 w-14 text-[#F59E0B]/20"
        fill="currentColor"
      />
      <Sparkles className="absolute top-4 left-4 h-4 w-4 text-[#F59E0B]/55" />
      <Sparkles className="absolute top-10 right-10 h-3 w-3 text-[#E11D48]/40" />
      <Stars className="absolute bottom-6 left-8 h-4 w-4 text-[#F59E0B]/45" />
    </>
  );
}

// ─── Variante Spotify — escura, imersiva ────────────────────────────────────

interface SpotifyVariantProps extends VariantInnerProps {
  palette: GiftTreePalette;
}

function SpotifyVariant({
  palette,
  gifts,
  openGift,
  openId,
  onSelect,
  onClose,
  className,
  title,
  subtitle,
}: SpotifyVariantProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "gift-tree-spotify relative overflow-hidden rounded-2xl",
        className
      )}
      style={{
        background: `radial-gradient(120% 100% at 0% 0%, rgba(${palette.accentRgb},0.22) 0%, rgba(${palette.accentRgb},0.06) 45%, rgba(15,15,18,0.95) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.25)`,
      }}
    >
      <SpotifyDecoration palette={palette} />

      <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-5">
        <header className="flex items-center gap-2">
          <span style={{ color: palette.accent }}>
            <GiftIcon className="h-3.5 w-3.5" />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: palette.accent }}
          >
            Árvore de presentes
          </span>
        </header>

        <div className="flex flex-col gap-1">
          <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
            {title || "Presentes dessa história"}
          </h3>
          <p className="text-[11px] text-white/60 leading-relaxed">
            {subtitle ||
              "Toque em cada presente para revelar um detalhe simbólico de vocês."}
          </p>
        </div>

        <SpotifyTreeVisual
          gifts={gifts}
          openId={openId}
          onSelect={onSelect}
          palette={palette}
        />

        <AnimatePresence mode="wait">
          {openGift ? (
            <motion.div
              key={openGift.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22 }}
              className="relative rounded-xl px-3 py-3 backdrop-blur"
              style={{
                background: `rgba(${palette.accentRgb},0.10)`,
                border: `1px solid rgba(${palette.accentRgb},0.24)`,
              }}
            >
              <button
                type="button"
                onClick={onClose}
                aria-label="Fechar presente"
                className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-white/60 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div
                className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{ color: palette.accent }}
              >
                <openGift.Icon className="h-3 w-3" />
                {openGift.title}
              </div>
              <p className="mt-1.5 pr-6 text-[12px] leading-relaxed text-white/85">
                {openGift.description}
              </p>
            </motion.div>
          ) : (
            <motion.p
              key="hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[10px] uppercase tracking-[0.2em] text-white/45"
            >
              Escolha um presente para abrir
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </motion.section>
  );
}

function SpotifyTreeVisual({
  gifts,
  openId,
  onSelect,
  palette,
}: {
  gifts: SymbolicGift[];
  openId: string | null;
  onSelect: (id: string) => void;
  palette: GiftTreePalette;
}) {
  return (
    <div className="relative mx-auto flex w-full max-w-[240px] flex-col items-center">
      <svg viewBox="0 0 200 220" width="180" height="200" aria-hidden="true">
        <defs>
          <linearGradient
            id="gift-tree-spotify-fill"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor={palette.accent} stopOpacity="0.9" />
            <stop offset="60%" stopColor={palette.accent} stopOpacity="0.55" />
            <stop offset="100%" stopColor={palette.accent} stopOpacity="0.18" />
          </linearGradient>
        </defs>

        <rect
          x="93"
          y="172"
          width="14"
          height="32"
          rx="3"
          fill="rgba(255,255,255,0.18)"
        />

        <path
          d="M100 12 L150 70 L120 70 L162 130 L130 130 L175 178 L25 178 L70 130 L38 130 L80 70 L50 70 Z"
          fill="url(#gift-tree-spotify-fill)"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth="1"
          strokeLinejoin="round"
        />

        <g transform="translate(100 8)">
          <circle r="8" fill="rgba(255,255,255,0.18)" />
          <path
            d="M0 -6 L1.4 -2 L5.8 -2 L2.2 0.7 L3.6 5 L0 2.4 L-3.6 5 L-2.2 0.7 L-5.8 -2 L-1.4 -2 Z"
            fill="#ffffff"
          />
        </g>

        {[
          { cx: 74, cy: 92, r: 1.8, delay: 0 },
          { cx: 124, cy: 86, r: 1.6, delay: 0.4 },
          { cx: 60, cy: 146, r: 1.8, delay: 0.8 },
          { cx: 140, cy: 150, r: 1.8, delay: 1.2 },
          { cx: 100, cy: 110, r: 1.5, delay: 0.6 },
        ].map((light, i) => (
          <motion.circle
            key={i}
            cx={light.cx}
            cy={light.cy}
            r={light.r}
            fill="#ffffff"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{
              duration: 2.4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: light.delay,
            }}
          />
        ))}
      </svg>

      <div className="mt-3 grid w-full grid-cols-2 gap-2">
        {gifts.map((g) => {
          const isActive = openId === g.id;
          const Icon = g.Icon;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => onSelect(g.id)}
              aria-label={`Abrir presente: ${g.title}`}
              aria-pressed={isActive}
              className="group flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-center transition-all backdrop-blur focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 hover:-translate-y-0.5"
              style={{
                background: isActive
                  ? `rgba(${palette.accentRgb},0.18)`
                  : `rgba(${palette.accentRgb},0.06)`,
                border: `1px solid rgba(${palette.accentRgb},${
                  isActive ? 0.45 : 0.18
                })`,
              }}
            >
              <span
                className="inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors"
                style={{
                  background: isActive
                    ? palette.accent
                    : `rgba(${palette.accentRgb},0.18)`,
                  color: isActive ? "#0F0F12" : palette.accent,
                }}
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/85">
                {g.title}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SpotifyDecoration({ palette }: { palette: GiftTreePalette }) {
  return (
    <>
      <Sparkles
        className="absolute top-3 right-3 h-3.5 w-3.5"
        style={{ color: palette.accent, opacity: 0.55 }}
      />
      <Stars
        className="absolute bottom-3 left-3 h-3 w-3"
        style={{ color: palette.accent, opacity: 0.4 }}
      />
    </>
  );
}
